'use client';
import { useEffect, useState } from 'react';
import parse from 'html-react-parser';
import { usePathname, useSearchParams } from 'next/navigation';
import { updateSearchParams } from '@overlapdev/functions';
import { AnimateWrapper } from '@overlapdev/wrappers';
import { Button } from '@overlapdev/elements';
import Image from 'next/image';
import Link from 'next/link';
import LocationMapMap from './map';
import { ContentType, PageType, TaxonomyType } from '@overlapdev/types';
import { LoadingSVG } from '@overlapdev/elements/assets';
import { CircleArrowSVG, ListSVG, LoadMoreSVG, MapSVG, RestoreLogoSVG } from 'components/svg';

const LocationMapResults = ({ content, no_tabs, post_type, taxonomy }: { content?: ContentType; no_tabs: boolean; post_type: string; taxonomy?: TaxonomyType[] }) => {
	//__Variables
	const posts_per_page = 9;
	const taxonomy_slugs = taxonomy?.map((e) => e.slug);
	const { body_content, ctas, heading, heading_tag, subheading } = content || {};

	//__Hooks
	const pathname = usePathname();
	const searchParams = useSearchParams();

	//__State
	const [filters, setFilters] = useState<any>({});
	const [loaded, setLoaded] = useState(false);
	const [page, setPage] = useState(1);
	const [results, setResults] = useState<PageType[]>([]);
	const [total, setTotal] = useState(0);
	const [updating, setUpdating] = useState(false);
	const [view, setView] = useState('list');

	//__Functions
	const updateResults = async (offset: number, filters: any, view?: string, amount?: number) => {
		setUpdating(true);
		const query: any = {
			amount: view === 'map' ? -1 : amount,
			offset,
			posts_per_page,
			post_type
		};
		//Add each filter to main query individually
		Object.keys(filters).map((item) => {
			if (filters[item]?.length) {
				query[item] = filters[item];
			}
		});
		const updatedResults = await fetch(`/api/location-map?query=${JSON.stringify(query)}`).then((res) => res.json());
		if (offset === 0) {
			//Reset results
			setResults(updatedResults?.posts);
		} else {
			//Append new results to existing results
			if (updatedResults?.posts?.length) {
				setResults((results) => [...results, ...updatedResults?.posts]);
			}
		}
		setTotal(updatedResults.total || 0);
		setUpdating(false);
	};

	const updatePage = () => {
		const new_page = page + 1;
		if (new_page.toString() !== searchParams.get('p')) {
			window.history.pushState(null, '', `${pathname}?${updateSearchParams(searchParams.toString(), 'p', new_page === 1 ? '' : new_page)}`);
		}
	};

	const updateView = (value: string) => {
		if (value !== searchParams.get('v')) {
			window.history.pushState(null, '', `${pathname}?${updateSearchParams(searchParams.toString(), 'v', value, 'p')}`);
		}
	};

	//__Use Effect
	useEffect(() => {
		const page_param = searchParams.has('p') ? parseInt(searchParams.get('p') || '', 10) : 1;
		const view_param = searchParams.has('v') ? searchParams.get('v')?.toString() : 'list';

		//Filter Router query to only include taxonomy slugs
		const filters_params: any = {};
		searchParams.forEach((value, key) => {
			if (taxonomy_slugs?.includes(key)) {
				filters_params[key] = value;
			}
		});

		if (!loaded) {
			const amount = page_param * posts_per_page;
			updateResults(0, filters_params, view_param, amount);
		} else {
			//If search or filters or date are changed
			if (JSON.stringify(filters_params) !== JSON.stringify(filters) || view_param !== view) {
				updateResults(0, filters_params, view_param);
			} else if (page_param !== page) {
				const offset = (page_param - 1) * posts_per_page;
				updateResults(offset, filters_params);
			}
		}

		setFilters(filters_params);
		setPage(page_param);
		setView(view_param || 'list');
		setLoaded(true);
	}, [searchParams]);

	return (
		<div className='relative'>
			{!no_tabs ? (
				<div className='absolute flex gap-1 left-0 -top-13'>
					<div className={`border cursor-pointer flex font-semibold gap-1.5 items-center px-5 py-[9px] rounded-full text-black ${view === 'list' ? 'bg-bright-green border-transparent' : 'bg-white border-black/10'}`} role='button' onClick={() => updateView('list')}>
						<ListSVG width={22} />
						<span>List</span>
					</div>
					<div className={`border cursor-pointer flex font-semibold gap-1.5 items-center px-5 py-[9px] rounded-full text-black ${view === 'map' ? 'bg-bright-green border-transparent' : 'bg-white border-black/10'}`} role='button' onClick={() => updateView('map')}>
						<MapSVG width={21} />
						<span>Map</span>
					</div>
				</div>
			) : null}
			<div className='gutter-x my-14 md:my-32'>
				<div className='inner'>
					{subheading ? <AnimateWrapper animate_type='fadeup' element='h6' className='mb-5' dangerouslySetInnerHTML={{ __html: subheading }} /> : null}
					<div className={`flex gap-3 justify-between max-md:flex-col md:gap-10`}>
						<div className='md:w-1/2'>{heading ? <AnimateWrapper animate_type='fadeup' element={heading_tag || 'h2'} className='text-black' dangerouslySetInnerHTML={{ __html: heading }} /> : null}</div>
						{body_content ? <AnimateWrapper animate_type='fadeup' element='div' className={`font-semibold [&_h2]:mb-3 [&_h3]:mb-3 [&_h4]:mb-3 [&_li]:font-medium [&_li]:text-base text-xl md:text-2xl md:w-1/2`} dangerouslySetInnerHTML={{ __html: body_content }} /> : null}
					</div>
					{ctas?.length ? (
						<AnimateWrapper animate_type='fadeup' element='div' className='mt-10'>
							{ctas.map((cta, index) =>
								cta.link?.url ? (
									<Button key={`regular_content_cta-${index}`} link={cta.link} className={`btn ${cta.style}`}>
										<CircleArrowSVG width={18} />
									</Button>
								) : null
							)}
						</AnimateWrapper>
					) : null}
					<div className='font-semibold mb-4 mt-10 text-right'>{total} Results</div>
					{updating ? (
						<div className='absolute flex justify-center top-0 w-full'>
							<LoadingSVG width={200} fill='#00548E' />
						</div>
					) : loaded && !results?.length ? (
						<div className='max-lg:min-h-[calc(80svh-94px)]'>
							<h6 className='text-center'>There are no locations matching your filters. Please clear the filters and try again.</h6>
						</div>
					) : null}
					<div className={view === 'map' && !no_tabs ? 'hidden' : ''}>
						{results?.length ? (
							<div className='gap-5 grid grid-cols-1 md:grid-cols-2 lg:gap-10 lg:grid-cols-3'>
								{results.map((item) => {
									const { acf, id, post_title, relative_path } = item || {};
									const { address, alternate_detail_page, grid_image, phone_number, alternate_address_link } = acf || {};
									const { city, post_code, state, state_short, street_name, street_name_short, street_number } = address || {};

									const detail_page = alternate_detail_page?.url ? alternate_detail_page.url : relative_path;

									return (
										<AnimateWrapper key={`location-map-${id}`} animate_type='fade' element='div' className=''>
											<div className='flex flex-col group h-full overflow-hidden relative rounded-3xl transition-ease hover:shadow-[30px_35px_55px_0px_rgba(46,46,46,0.23)]'>
												<div className='overflow-hidden relative h-[220px] sm:h-[320px] md:h-[280px]'>
													<Link href={detail_page} target={alternate_detail_page?.target || '_self'}>
														{grid_image?.url ? (
															<Image src={grid_image.url} alt={grid_image.alt} fill className='object-cover object-[50%_20%] transition-ease md:group-hover:scale-110' sizes='960px' />
														) : (
															<div className='bg-black/10 flex h-full items-center justify-center p-4 lg:p-10'>
																<RestoreLogoSVG className='opacity-60' fill='#fff' />
															</div>
														)}
													</Link>
												</div>
												<div className='bg-white border-b border-x border-black/10 flex flex-col gap-5 grow p-8 rounded-b-3xl lg:p-10'>
													<h6 className='text-black'>{parse(post_title)}</h6>
													<div className='flex flex-col gap-2 grow items-start'>
														{address ? (
															<div>
																<div className='mb-1'>Address</div>
																<div className='text-black font-semibold'>
																	<Link href={alternate_address_link || `https://maps.google.com/?q=${`${street_number} ${street_name_short || street_name}, ${city}, ${state_short || state} ${post_code}`}`} target='_blank' className='text-dark-blue'>
																		{street_number} {street_name_short || street_name}, {city}, {state_short || state} {post_code}
																	</Link>
																</div>
															</div>
														) : null}
														{phone_number ? (
															<div>
																<div className='mb-1'>Phone Number</div>
																<a href={`tel:${phone_number}`} className='text-dark-blue font-semibold'>
																	{phone_number}
																</a>
															</div>
														) : null}
													</div>
													<div className=''>
														<Button link={{ url: detail_page, title: 'See Details', target: alternate_detail_page?.url || '_self' }} className='arrow_link'>
															<CircleArrowSVG width={18} />
														</Button>
													</div>
												</div>
											</div>
										</AnimateWrapper>
									);
								})}
							</div>
						) : null}
						{results?.length < total ? (
							<div className='mt-10 text-center md:mt-16'>
								<Button className='arrow_link cursor-pointer no_animate' link={{ title: 'Load More', url: '' }} onClick={() => updatePage()}>
									<LoadMoreSVG className='text-black' width={20} />
								</Button>
							</div>
						) : null}
					</div>
				</div>
			</div>
			<LocationMapMap className={(view === 'list' || !results?.length) && !no_tabs ? 'hidden' : ''} locations={results} map_className='h-200' />
		</div>
	);
};

export default LocationMapResults;
